import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Grid, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziGrid, { Constants, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { format, isValid, parse } from "date-fns";
import { useActionFolders } from "Hooks/useActionFolders";
import { useTranslation } from "react-i18next";
import { BiDislike, BiLike } from "react-icons/bi";
import { FiActivity, FiCalendar, FiEdit, FiEye, FiMenu, FiVideo, FiPrinter } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";
import { IoWarningOutline } from "react-icons/io5";
import { MdOutlineCancel } from "react-icons/md";
import { VscError } from "react-icons/vsc";
import { useHistory } from "react-router-dom";

import ConfirmationModal from "../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../components/Form/Button";
import Chip from "../../../../components/Form/Chip";
import AccessTab from "../../../../components/Generic/Access/ViewMode";
import BadgeSubStatus from "../../../../components/Generic/BadgeSubStatus";
import BadgeType from "../../../../components/Generic/BadgeType";
import Files from "../../../../components/Generic/Files";
import History from "../../../../components/Generic/History";
import Instructions from "../../../../components/Generic/Instructions";
import Modal, { ModalType } from "../../../../components/Generic/Modal";
import PackingMaterials from "../../../../components/Generic/PackingMaterials";
import { NotificationToast } from "../../../../components/Generic/Toast/NotificationToast";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../components/Generic/ViewJobComponent";
import TitlePage from "../../../../components/TitlePage";
import { PRIVATE_ROUTES, SELECTED_COLUMNS_QUOTES } from "../../../../constants";
import { useAppState } from "../../../../context/StateContext";
import { datesAreSame } from "../../../../helpers/helpers";
import { useActionsJob } from "../../../../hooks/useActionJob";
import { Customer } from "../../../../models/customer";
import { Destination } from "../../../../models/destination";
import { Error as IError } from "../../../../models/error";
import { File } from "../../../../models/file";
import { Job } from "../../../../models/job";
import { JobType } from "../../../../models/jobType";
import { LeadSource } from "../../../../models/leadsource"; // 1 hour
import { Location } from "../../../../models/location";
import { Sales } from "../../../../models/sales";
import { Filters, Searches, Sort } from "../../../../models/shipeezi.table";
import { Statuses } from "../../../../models/statuses";
import { Task } from "../../../../models/task";
import { User } from "../../../../models/user";
import AmosService from "../../../../services/amos.service";
import DestinationService from "../../../../services/destination.service";
import JobTypeService from "../../../../services/job.type.service";
import LeadSourceFromService from "../../../../services/lead.source.from.service";
import OrderService from "../../../../services/order.service";
import QuoteService from "../../../../services/quote.service";
import ScreenshotService from "../../../../services/screenshot.service";
import StatusesService from "../../../../services/statuses.service";
import TaskService from "../../../../services/task.service";
import { isJobQuote, isSubStatusAvailableToActionsQuote } from "../../../../util/actions";
import { calculateTotalVolume } from "../../../../util/calculateTotalVolume";
import { checkError } from "../../../../util/checkError";
import { Metric, handleGetConvertValue } from "../../../../util/converts";
import {
  ItemJobSubStatus,
  JobStatus,
  JobSubStatus,
  Status,
  SubStatusUsedBy,
  TabItem,
  TaskType,
  UsedInEmailTemplates,
} from "../../../../util/enums";
import env from "../../../../util/env";
import getQuoteFolder from "../../../../util/getFolders";
import getItemsToHistory from "../../../../util/getHistory";
import { getMeasurement } from "../../../../util/getMeasurement";
import { handlesGrid } from "../../../../util/handlesGrid";
import { isAllied } from "../../../../util/isAllied";
import Calendar from "./Calendar";
import SkeletonCommunication from "./Calendar/skeleton";
import CancelQuoteConfirmationModal, {
  CancelQuoteConfirmationModalHandles,
} from "./CancelConfirmationModal";
import DetailsViewMode from "./Maintenance/Details/Form/ViewMode";
import Inventory from "./Maintenance/Inventory";
import LoadingSkeleton from "./Maintenance/skeleton";
import SummaryTab from "./Maintenance/Summary";
import Tasks from "./Maintenance/Tasks";
import VideoSurvey from "./Maintenance/VideoSurvey";
import SelectSurveyTaskModal from "./SelectSurveyTaskModal";
import useStyles, {
  Icon,
  ModelTextDescription,
  NoDataContainer,
  NoDataTitle,
  TagWrapper,
} from "./styles";

import "./index.scss";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";
import { GenerateReportDrawer, GenerateReportDrawerHandles } from "Components/GenerateReport";
import { filterVideoSurveyFolder } from "Util/filterVideoSurvey";
import SendToCustomer, { SendToCustomerHandle } from "./SendToCustomer";
import SendToCustomerModalQuote, { SendToCustomerQuoteHandle } from "./SendToCustomerModalQuote";

const PAPER_EDIT = require("../../../../assets/img/paper-edit.svg");

const STALE_TIME = 1 * 60 * 1000; // 1 minute
const CACHE_TIME = 1 * 60 * 60 * 1000;

type Props = {
  showTitle?: boolean;
  className?: string;
  classNameContent?: string;
  customFilter?: { [x: string]: string | number | undefined };
  viewOnly?: boolean;
  isRefetchGetAll?: boolean;
  orderByGeneralActions?: string[];
  handleNewRecord?: () => void;
  handleEdit?: (id: number, quote: Job) => void;
};

const Quote = (props: Props) => {
  const {
    showTitle,
    className,
    classNameContent,
    customFilter,
    viewOnly,
    orderByGeneralActions,
    handleNewRecord,
    handleEdit,
    isRefetchGetAll,
  } = props;

  const { currentUser, userPermissions } = useAppState();
  const classes = useStyles();
  const history = useHistory();
  const { t: translate } = useTranslation();
  const measurement = getMeasurement();

  const [showCalendarFormat, setShowCalendarFormat] = useState<boolean>(false);
  const generateReportRef = useRef<GenerateReportDrawerHandles>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [toViewQuote, setToViewQuote] = useState<Job>();
  const [quote, setQuote] = useState<Sales>();

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);

  const [sorting, setSorting] = useState<Array<string[]>>();
  const [disableCustomerSorting, setDisableCustomerSorting] = useState<boolean>(false);

  const [tasksSelected, setTasksSelected] = useState<Task[]>();
  const [openModalSelectSurveyTask, setOpenModalSelectSurveyTask] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [leadSourceFrom, setLeadSourceFrom] = useState<LeadSource[]>([]);

  const [quoteIdSend, setQuoteIdSend] = useState<number>();
  const [anchorMoreIcon, setAnchorMoreIcon] = useState<null | HTMLElement>(null);

  const [screenshotsFiles, setScreenshotsFiles] = useState<File[]>([]);
  const [recordingsFiles, setRecordingsFiles] = useState<File[]>([]);
  const [enableQuoteEditing, setEnableQuoteEditing] = useState<boolean>(false);
  const [cancelOrderAsWell, setCancelOrderAsWell] = useState<boolean>(false);

  const [quoteToBeEdit, setQuoteToBeEdit] = useState<Job>();
  const [quoteHasOrder, setQuoteHasOrder] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<number>();

  const [newFolders, setNewFolders] = useState<any[]>([]);

  const savedColumns = window.localStorage.getItem(SELECTED_COLUMNS_QUOTES);
  const columnsToShow: string[] = savedColumns ? JSON.parse(savedColumns) : [];

  const dateTimeFormat = useMemo(() => {
    const userDatePreference = currentUser?.preferences?.dateFormat;
    const userTimePreference = currentUser?.preferences?.timeFormat;

    const preferredDateFormat = userDatePreference === "MM_DD_YYYY" ? "MM/dd/yyyy" : "dd/MM/yyyy";
    const preferredTimeFormat = userTimePreference === "12_HOUR" ? "hh:mm a" : "HH:mm";

    return `${preferredDateFormat} ${preferredTimeFormat}`;
  }, [currentUser?.preferences]);

  const formatDateWithUserPref = useCallback(
    (value: any) => {
      if (!value) return "";

      let date = new Date(value);

      if (!isValid(date)) {
        date = parse(value.toLowerCase(), "dd/MM/yyyy hh:mmaaa", new Date());
      }

      return isValid(date) ? format(date, dateTimeFormat) : value;
    },
    [dateTimeFormat]
  );

  const sideViewRef = useRef<PropHandles>(null);

  const { handleCurrentPage, handleFilter, handlePageSize, handleSearch, handleSort } = handlesGrid(
    {
      setFilters,
      setSorting,
      setPage,
      setPageSize,
      setSearches,
    }
  );

  const { getInventoryList, getJobHistories } = useActionsJob({
    jobId: toViewQuote?.id,
  });

  const { data: inventoryData, isLoading: isLoadingInventory } = getInventoryList([
    ItemJobSubStatus.QUOTED,
  ]);
  const { data: historyData } = getJobHistories();

  const buildRoomsFolder = useCallback((inventory: any[], jobId?: number, companyId?: number) => {
    if (!inventory?.length) return null;

    const roomsFolders = inventory
      .filter((room) => room?.roomPictures?.length)
      .map((room) => ({
        type: "Photos & Videos",
        name: room?.name || "Room",
        isAllowedEdit: false,
        isAllowedDelete: false,
        jobId,
        companyId,
        parentFolderId: null,
        showQuantity: false,
        folders: [],
        files: (room.roomPictures || [])
          .filter((p: any) => !!p?.mediaLink)
          .map((picture: any, index: number) => ({
            name: picture?.name || `Photo ${index + 1}`,
            uri: picture?.mediaLink,
            type: "image",
            extension: (picture?.mediaLink?.split(".").pop() || "png").split("?")[0],
            isAllowedDelete: false,
            isAllowedEdit: false,
          })),
      }))
      .filter((r) => r?.files?.length);

    if (!roomsFolders.length) return null;

    return {
      type: "Photos & Videos",
      name: "Rooms",
      isAllowedEdit: false,
      isAllowedDelete: false,
      jobId,
      companyId,
      parentFolderId: null,
      showQuantity: false,
      folders: roomsFolders,
      files: [],
    };
  }, []);

  const normalizeFolderName = (value?: string) => (value ?? "").trim().toLowerCase();

  const addRoomsFolderInsidePhotosVideosQuote = useCallback(
    (currentFolders: any[] = [], roomsFolder: any | null) => {
      if (!roomsFolder) return currentFolders;

      const foldersList = Array.isArray(currentFolders) ? currentFolders : [];

      return foldersList.map((rootFolder) => {
        const rootName = normalizeFolderName(rootFolder?.name);

        const isPhotosRoot = rootName === "photos" || rootName === "photos & videos";
        if (!isPhotosRoot) return rootFolder;

        const photosSubFolders = Array.isArray(rootFolder?.folders) ? rootFolder.folders : [];

        const photosSubFoldersUpdated = photosSubFolders.map((subFolder: any) => {
          const subName = normalizeFolderName(subFolder?.name);

          if (subName !== "quote") return subFolder;

          const quoteChildrenFolders = Array.isArray(subFolder?.folders) ? subFolder.folders : [];

          const alreadyHasRooms = quoteChildrenFolders.some(
            (child: any) => normalizeFolderName(child?.name) === "rooms"
          );

          if (alreadyHasRooms) return subFolder;

          return {
            ...subFolder,
            folders: [...quoteChildrenFolders, roomsFolder],
          };
        });

        return {
          ...rootFolder,
          folders: photosSubFoldersUpdated,
        };
      });
    },
    []
  );

  const { getQuoteFolders } = useActionFolders({ jobId: toViewQuote?.id });
  const { data: folders } = getQuoteFolders();

  const videoSurveyTasks: Task[] = useMemo(() => {
    return quote?.tasks?.filter((task) => task.type === TaskType.VIDEO_SURVEY) || [];
  }, [quote?.tasks]);

  const [quotePermission, setQuotePermission] = useState<{
    viewAll: boolean;
    viewOwn: boolean;
    create: boolean;
    edit: boolean;
    approve: boolean;
    decline: boolean;
    videoSurvey: boolean;
    communication: boolean;
    cancel: boolean;
    editEstimate: boolean;
  }>({
    viewAll: false,
    viewOwn: false,
    create: false,
    edit: false,
    approve: false,
    decline: false,
    videoSurvey: false,
    communication: false,
    cancel: false,
    editEstimate: false,
  });

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ESTIMATES,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data?.length) setJobStatus(response.data);
      },
    }
  );

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response?.data?.length) setJobTypes(response.data);
      },
    }
  );

  useQuery(
    ["leadSource.list"],
    () =>
      LeadSourceFromService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response?.data?.length) setLeadSourceFrom(response.data);
      },
    }
  );

  const {
    data: quotes,
    isFetching: loading,
    refetch: refetchQuoteList,
  } = useQuery(
    ["quotes.list", page, pageSize, sorting, filters, searches, customFilter, showCalendarFormat],
    async () => {
      const response = await QuoteService.getAll({
        companyId: currentUser?.companyId,
        page,
        pageSize,
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        userId: currentUser?.id,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
        ...(customFilter || {}),
      });

      if (response?.data) return response?.data as Job[];
      return [];
    },
    {
      enabled: !!currentUser?.id && !showCalendarFormat,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const { data: total, refetch: refetchCountList } = useQuery(
    ["quotes.count", filters, searches, customFilter, showCalendarFormat],
    async () => {
      if (
        !isShowAction(currentUser, quotePermission.viewAll) &&
        !searches?.some((search) => search?.user === currentUser?.email)
      ) {
        searches?.push({ user: currentUser?.email || "" });
      }

      const response = await QuoteService.count({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
        ...(customFilter || {}),
      });

      return response?.data || 0;
    },
    {
      enabled:
        !!currentUser?.id &&
        !showCalendarFormat &&
        (isShowAction(currentUser, quotePermission?.viewAll) ||
          isShowAction(currentUser, quotePermission?.viewOwn)),
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const {
    data: quotesCalendar,
    isFetching: isFetchingCalendar,
    refetch: refetchQuoteCalendar,
  } = useQuery(
    ["quotes.calendar.list", filters, customFilter, showCalendarFormat, searches],
    async () => {
      const response = await QuoteService.getAll({
        companyId: currentUser?.companyId,
        calendar: new Date().toISOString(),
        userId: currentUser?.id,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
        ...(customFilter || {}),
      });

      if (response?.data) return response?.data;
      return [];
    },
    {
      enabled: !!currentUser?.id && showCalendarFormat,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const { mutate: approve, isLoading: approvingQuote } = useMutation(
    ["quote.approve"],
    ({ id }: { id: number }) => QuoteService.approve(id),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (showCalendarFormat) refetchQuoteCalendar();
        else {
          refetchQuoteList();
          refetchCountList();
        }
        setApproveJobId(0);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<IError>;
        NotificationToast.warning(errorAxios?.response?.data?.message);
      },
    }
  );

  const recordingMutate = useMutation(
    ["recording.get"],
    ({ taskId }: { taskId: number }) => TaskService.getRecordings(Number(taskId)),
    {
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;
        NotificationToast.warning(errorAxios?.response?.data?.message);
      },
    }
  );

  const { mutate: sendToAMOS, isLoading: loadingSendToAmos } = useMutation(
    ["amos.service.send.quote"],
    ({ jobId }: { jobId: number }) => AmosService.sendQuoteToAmos(jobId),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setQuoteIdSend(undefined);
      },
      onError: (error: AxiosError<IError>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
        setQuoteIdSend(undefined);
      },
    }
  );

  const { mutate: decline, isLoading: decliningQuote } = useMutation(
    ["quote.decline"],
    ({ id }: { id: number }) => QuoteService.decline(id),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (showCalendarFormat) refetchQuoteCalendar();
        else {
          refetchQuoteList();
          refetchCountList();
        }
        setDeclineJobId(0);
      },
      onError: (error) => {
        const errorAxios = error as AxiosError<IError>;
        NotificationToast.warning(errorAxios?.response?.data?.message);
      },
    }
  );

  const cancelModalRef = useRef<CancelQuoteConfirmationModalHandles>(null);
  const { mutate: cancelQuote, isLoading: loadingCancelQuote } = useMutation(
    ["quote.cancel"],
    ({ id, reason }: { id: number; reason: string }) => QuoteService.cancel(id, reason),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (quoteHasOrder) setCancelOrderAsWell(true);

        if (showCalendarFormat) refetchQuoteCalendar();
        else {
          refetchQuoteList();
          refetchCountList();
        }
        cancelModalRef.current?.close();
      },
    }
  );

  const { refetch, isFetching } = useQuery(
    ["quotes.get", toViewQuote?.id],
    () =>
      QuoteService.getById(Number(toViewQuote?.id || quote?.id), {
        isSalesApp: true,
        isNewVersion: true,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!toViewQuote?.id,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onSuccess: (response) => {
        if (!response?.data) return;

        const apiQuote = response.data;

        const foldersFromApiOrFallback = (apiQuote.folders ?? folders ?? []) as any[];

        const roomsFolder = buildRoomsFolder(
          apiQuote.inventory ?? [],
          apiQuote?.id,
          apiQuote?.companyId
        );

        const foldersWithRooms = addRoomsFolderInsidePhotosVideosQuote(
          foldersFromApiOrFallback,
          roomsFolder
        );

        const quoteWithFoldersPrepared = getQuoteFolder(translate, {
          ...apiQuote,
          folders: foldersWithRooms,
        });

        const foldersAfterVideoSurveyFilter = filterVideoSurveyFolder(
          ((quoteWithFoldersPrepared as any)?.folders ?? foldersWithRooms) as any[]
        );

        setQuote((prev) => ({
          ...prev,
          ...quoteWithFoldersPrepared,
          folders: foldersAfterVideoSurveyFilter,
        }));

        setNewFolders(foldersAfterVideoSurveyFilter);

        sideViewRef?.current?.setCurrentLink(Navigation.DETAILS);
        setRecordingsFiles([]);
        setScreenshotsFiles([]);

        if (
          apiQuote.jobSubStatus === JobStatus.CANCELLED ||
          apiQuote.jobSubStatus === JobStatus.LOST
        ) {
          localStorage.setItem("hasToOpen", "true");
        }
      },
    }
  );

  useQuery(
    ["quotes.tabs.video.survey.screenshot", videoSurveyTasks],
    () => ScreenshotService.getAll({ taskId: videoSurveyTasks?.map((task) => task?.id) }),
    {
      enabled: !!(videoSurveyTasks && videoSurveyTasks?.length > 0),
      onSuccess: (response) => {
        if (response && response.data)
          setScreenshotsFiles(
            response.data.map((screenshot, index) => ({
              name: `Screenshot ${index + 1}`,
              type: "image",
              uri: screenshot?.link,
              extension: "png",
              isAllowedDelete: false,
              isAllowedEdit: false,
            }))
          );
      },
      refetchOnWindowFocus: false,
    }
  );

  const getAllRecordings = async () => {
    if (videoSurveyTasks && videoSurveyTasks?.length > 0) {
      const allFiles: File[] = [];

      await Promise.all(
        videoSurveyTasks.map(async (task) => {
          if (task?.id) {
            const response = await recordingMutate.mutateAsync({ taskId: task?.id });
            const records = response?.data;

            if (records && records?.length > 0) {
              records.forEach((record, index) => {
                allFiles.push({
                  name: `${format(new Date(task?.startDate), "dd/MM/yyyy hh:mm aa")}${
                    records.length > 1 ? ` - Video ${index + 1}` : ""
                  }`,
                  type: "video",
                  uri: record?.link,
                  extension: "mp4",
                  isAllowedDelete: false,
                  isAllowedEdit: false,
                });
              });
            }
          }
        })
      );

      setRecordingsFiles(allFiles);
    }
  };

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setQuotePermission({
        viewAll: hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_VIEW_ALL),
        viewOwn: hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_VIEW_OWN),
        create:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_ADD_NEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_EDIT),
        approve:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_APPROVE),
        decline:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_DECLINE),
        videoSurvey:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_VIDEO_SURVEY),
        communication:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_COMMUNICATION),
        cancel:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_CANCEL_QUOTE),
        editEstimate:
          checkResourceCan(userPermissions, ResourceName.QUOTE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SALES_QUOTE_EDIT_ESTIMATE),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    if (!inventoryData) return;

    setQuote((prev) => {
      if (!prev) return prev;

      const quotedInventory = inventoryData?.quotedInventory ?? [];

      const roomsFolder = buildRoomsFolder(
        quotedInventory,
        (prev as any)?.id,
        currentUser?.companyId
      );

      const foldersCurrentlyInState = ((prev as any)?.folders ?? []) as any[];

      const foldersWithRooms = addRoomsFolderInsidePhotosVideosQuote(
        foldersCurrentlyInState,
        roomsFolder
      );

      const quoteWithFoldersPrepared = getQuoteFolder(translate, {
        ...prev,
        inventory: quotedInventory,
        folders: foldersWithRooms,
      });

      const foldersAfterVideoSurveyFilter = filterVideoSurveyFolder(
        ((quoteWithFoldersPrepared as any)?.folders ?? foldersWithRooms) as any[]
      );

      setNewFolders(foldersAfterVideoSurveyFilter);

      return {
        ...quoteWithFoldersPrepared,
        folders: foldersAfterVideoSurveyFilter,
      };
    });
  }, [
    inventoryData,
    buildRoomsFolder,
    addRoomsFolderInsidePhotosVideosQuote,
    currentUser?.companyId,
    translate,
  ]);

  useEffect(() => {
    if (historyData && historyData?.length) {
      setQuote((value) => ({ ...value, histories: historyData }));
    }
  }, [historyData]);

  useEffect(() => {
    if (videoSurveyTasks && videoSurveyTasks?.length > 0) {
      getAllRecordings();
    }
  }, [videoSurveyTasks]);

  const mutate = useMutation(
    ["quote.update"],
    () => QuoteService.update(Number(quote?.id), { ...quote, openedView: false }),
    {
      onSuccess: () => {
        setOpenModal(false);
        localStorage.removeItem("hasToOpen");
        refetch();
      },
    }
  );

  const { mutate: editableQuote, isLoading: isUpdatingToBeEditable } = useMutation(
    ["quote.update.to.be.editable"],
    () =>
      QuoteService.updateToBeEditable(Number(quoteToBeEdit?.id), {
        ...quoteToBeEdit,
        openedView: false,
      }),
    {
      onSuccess: () => {
        setEnableQuoteEditing(false);
        setQuoteToBeEdit(undefined);

        const location = {
          ...history.location,
          pathname: `${PRIVATE_ROUTES.QUOTES_MNT}/${quoteToBeEdit?.id}`,
          state: { tabItem: TabItem.ESTIMATE },
        };

        history.replace(location);
      },
    }
  );

  const listQuotes = showCalendarFormat ? quotesCalendar || [] : quotes || [];

  const declineQuote = (quoteItem: Job) => {
    if (quoteItem?.id) decline({ id: quoteItem.id });
  };

  const approveQuote = (quoteItem: Job) => {
    if (quoteItem?.id) approve({ id: quoteItem.id });
  };

  const cancelQuoteSubmit = (id: number, reason?: string) => {
    if (id && id > 0 && reason) cancelQuote({ id, reason });
  };

  const renderCancelQuoteButton = (_id: string | number, quoteItem: Job) => {
    const action = () => {
      if (quoteItem?.id) {
        if (quoteItem.status === JobStatus.BOOKED) {
          setQuoteHasOrder(true);
          setSelectedJobId(quoteItem.id);
        } else {
          setQuoteHasOrder(false);
          setSelectedJobId(undefined);
        }
        cancelModalRef.current?.open(quoteItem.id, quoteItem?.reference);
      }
    };

    if (quoteItem?.id) {
      if (isJobQuote(quoteItem) && isSubStatusAvailableToActionsQuote(quoteItem.subStatus)) {
        return (
          <Tooltip title={translate("cancelQuote")} placement="top">
            <div>
              <MdOutlineCancel
                size={Constants.defaultGeneralActionsIconSize}
                className={classes.button}
                data-cy="canceledButton"
                color="#F97066"
                onClick={() => action()}
              />
            </div>
          </Tooltip>
        );
      }
    }

    return null;
  };

  const editTask = (id: string | number) => {
    history.push(`${PRIVATE_ROUTES.QUOTES_MNT}/${Number(id)}`);
  };

  const newFoldersToRender = useMemo(() => {
    return filterVideoSurveyFolder(newFolders);
  }, [newFolders]);

  const getVideoTasks = (quoteItem: Job) => {
    const now = new Date();

    return quoteItem?.tasks?.filter((task: Task) => {
      if (!task?.type?.toLowerCase()?.includes("video")) return false;
      if (!task.startDate) return false;

      const startDate = new Date(task.startDate);
      if (Number.isNaN(startDate.getDate())) return false;

      const equals = datesAreSame(startDate, now);
      if (equals == null) return false;

      return equals;
    });
  };

  const quoteHasVideoSurvey = (quoteItem: Job) => {
    return quoteItem
      ? quoteItem?.tasks?.find((task) => task.type.toLowerCase().includes("video survey"))
      : null;
  };

  const startVideoSurveyButton = (id: string | number, quoteItem?: Job) => {
    if (
      !viewOnly &&
      quoteItem &&
      isJobQuote(quoteItem) &&
      isSubStatusAvailableToActionsQuote(quoteItem?.subStatus, false, true)
    ) {
      if (quoteHasVideoSurvey(quoteItem)) {
        const tasks = getVideoTasks(quoteItem);

        if (tasks && tasks?.length > 0) {
          return (
            <Tooltip title={translate("startVideoSurvey") || ""} placement="top">
              <div>
                <FiVideo
                  className={classes.button}
                  size={Constants.defaultGeneralActionsIconSize}
                  color="#38CFC4"
                  onClick={() => {
                    setTasksSelected(tasks);
                    setOpenModalSelectSurveyTask(true);
                  }}
                />
              </div>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={translate("startVideoSurvey") || ""} placement="top">
            <div>
              <FiVideo
                className={classes.button}
                size={Constants.defaultGeneralActionsIconSize}
                color="#38CFC4"
                onClick={() => {
                  setEditTaskId(id);
                  setOpenRescheduleModal(true);
                }}
              />
            </div>
          </Tooltip>
        );
      }
    }

    return null;
  };

  const selectTaskToSendingCommunications = (id: string | number) => {
    const found = listQuotes?.find((q: Job) => Number(q.id) === Number(id));

    if (!found) return;

    const videoSurveys = getVideoTasks(found);

    setTasksSelected(videoSurveys ?? []);

    // Use new communication modal for unified email/SMS/push notifications
    // communicationModalRef?.current?.onOpen(Number(id), found, found.customer);
    sendToCustomerRef?.current?.onOpen(Number(id), found, found.customer);
  };

  const showBadgeType = (value?: string) => <BadgeType value={value} />;
  const showBadgeSubStatus = (value?: string) => <BadgeSubStatus value={value} />;

  const renderTextLocation = (locations?: Location[]) => {
    if (locations && locations?.length > 0)
      return locations[0]?.longAddress?.toString()?.trim() || "";
    return "";
  };

  const renderOrigin = (locations?: Location[]) => (
    <div className={classes.containerLocation}>
      <div className="flex-location">
        <div className="div-location-ellipsis">
          <span>{renderTextLocation(locations)}</span>
        </div>
      </div>
    </div>
  );

  const renderDestination = (locations?: Location[]) => (
    <div className={classes.containerLocation}>
      <div className="flex-location">
        <div className="div-location-ellipsis">
          <span>{renderTextLocation(locations)}</span>
        </div>
      </div>
    </div>
  );

  const [openRescheduleModal, setOpenRescheduleModal] = useState<boolean>(false);
  const [editTaskId, setEditTaskId] = useState<number | string>(0);

  const renderReScheduleVideoSurveyModal = () => {
    if (!openRescheduleModal) return null;

    return (
      <Modal
        className={classes.rescheduleModal}
        open={openRescheduleModal}
        type={ModalType.INFO}
        title={translate("videoSurveyUnavailable")}
        showActionButtons
        handleClose={(_, reason) => {
          if (reason !== "backdropClick") setOpenRescheduleModal(false);
        }}
        actionButtonProps={{
          textSave: translate("scheduleNow"),
          customColorSave: Color.SECONDARY,
          loading: mutate.isLoading,
          onCancel: () => setOpenRescheduleModal(false),
          onSave: () => {
            const location = {
              pathname: `${PRIVATE_ROUTES.QUOTES_MNT}/${Number(editTaskId)}`,
              state: { tabItem: "Date" },
            };

            history.replace(location);
          },
        }}
      >
        <p className={classes.messageModal}>
          {translate("thisVideoSurveyHasBeenClosed")}.{" "}
          {translate("pleaseScheduleAnotherSurveyToContinue")}.
        </p>
        <span className={classes.subMessageVideoSurveyUnavailable}>
          {translate("youCanScheduleAnotherVideoSurveyNowOrLater")}
        </span>
      </Modal>
    );
  };

  const renderSelectVideoSurveyModal = () => {
    if (!openModalSelectSurveyTask) return null;

    return (
      <SelectSurveyTaskModal
        open={openModalSelectSurveyTask}
        tasks={tasksSelected}
        handleClose={() => {
          setOpenModalSelectSurveyTask(false);
          setTasksSelected(undefined);
        }}
      />
    );
  };

  const [approveJobId, setApproveJobId] = useState(0);
  const renderApproveConfirmationModal = () => {
    if (!approveJobId) return null;

    const found = listQuotes?.find((q: Job) => Number(q.id) === Number(approveJobId));
    if (!found?.id) return null;

    return (
      <ConfirmationModal
        open={!!approveJobId}
        title={translate("approveQuote")}
        closeModal={() => setApproveJobId(0)}
        loading={approvingQuote}
        submit={() => approveQuote(found)}
        textSubmit={translate("approve")}
      >
        <Grid container className={classes.approveContent}>
          <Grid item>
            <span className={classes.approveDescription}>
              {translate("doYouReallyWantToApproveTheQuote")}
            </span>
          </Grid>
        </Grid>
      </ConfirmationModal>
    );
  };

  const renderEnableQuoteEditingModal = () => {
    return enableQuoteEditing ? (
      <ConfirmationModal
        open={enableQuoteEditing}
        type="warning"
        title={translate("enableEstimateEditing")}
        leftIconTitle={<IoWarningOutline color="#F79009" size={20} />}
        iconDivClass={classes.iconDiv}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.WARNING}
        textSubmit={translate("enable")}
        closeModal={() => setEnableQuoteEditing(false)}
        loading={isUpdatingToBeEditable}
        submit={() => editableQuote()}
        width="520px !important"
      >
        <Grid container direction="column" className={classes.approveContent}>
          <Grid item>
            <ModelTextDescription className={classes.approveDescription}>
              {translate("areYouSureYouWantToEnableEditingEstimateForThisQuote")}
            </ModelTextDescription>
          </Grid>
          <Grid item>
            <ModelTextDescription className={classes.approveDescription}>
              {translate("changesMadeToThisQuoteMayAlsoAffectOrder")}
            </ModelTextDescription>
          </Grid>
        </Grid>
      </ConfirmationModal>
    ) : null;
  };

  const { mutate: orderCancel } = useMutation(
    ["order.status.cancel"],
    ({ id, reason }: { id: number; reason: string }) => OrderService.cancel(id, { reason }),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setCancelOrderAsWell(false);
        setQuoteHasOrder(false);
        setSelectedJobId(undefined);
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const renderCancelOrder = () => {
    return cancelOrderAsWell && quoteHasOrder ? (
      <ConfirmationModal
        open={cancelOrderAsWell}
        type="error"
        title={translate("cancelOrder")}
        leftIconTitle={<VscError color="#F04438" size={20} />}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.ERROR}
        textSubmit={translate("cancel")}
        closeModal={() => {
          setQuoteHasOrder(false);
          setCancelOrderAsWell(false);
        }}
        submit={() => orderCancel({ id: Number(selectedJobId), reason: "cancel order as well" })}
        width="520px !important"
      >
        <Grid container direction="column" className={classes.approveContent}>
          <Grid item>
            <ModelTextDescription className={classes.approveDescription}>
              {translate("doYouAlsoWantToCancelTheLinkedOrder")}?
            </ModelTextDescription>
          </Grid>
        </Grid>
      </ConfirmationModal>
    ) : null;
  };

  const approveButton = (_id: string | number, quoteItem: Job) => {
    if (
      !viewOnly &&
      isJobQuote(quoteItem) &&
      isSubStatusAvailableToActionsQuote(quoteItem?.subStatus)
    ) {
      return (
        <Tooltip title={translate("approve") || ""} placement="top">
          <div>
            <BiLike
              onClick={() => setApproveJobId(Number(quoteItem.id))}
              className={classes.button}
              color="#32D583"
              size={Constants.defaultGeneralActionsIconSize}
            />
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const renderSeeMore = (_id: string | number, quoteItem: Job) => {
    if (loadingSendToAmos && quoteIdSend === quoteItem?.id)
      return <CircularProgress size={18} className={classes.loadingSendToAMOS} />;

    const taskVideoSurveyHomeSurvey = quoteItem?.tasks?.length
      ? quoteItem?.tasks?.find(
          (task) => task?.type === TaskType.VIDEO_SURVEY || task?.type === TaskType.HOME_SURVEY
        )
      : undefined;

    if (
      !viewOnly &&
      taskVideoSurveyHomeSurvey &&
      quoteItem?.id &&
      quoteItem?.subStatus !== JobSubStatus.DRAFT &&
      isAllied(currentUser?.company) &&
      env.NODE_DATABASE === "production"
    ) {
      return (
        <IconButton
          onClick={(event) => {
            setAnchorMoreIcon(event.currentTarget);
            setQuoteIdSend(quoteItem?.id);
          }}
          size="small"
        >
          <MoreVertIcon htmlColor="#475467" fontSize="small" />
        </IconButton>
      );
    }

    return null;
  };

  const [declineJobId, setDeclineJobId] = useState(0);
  const renderDeclineConfirmationModal = () => {
    if (!declineJobId) return null;

    const found = listQuotes?.find((q: Job) => Number(q.id) === Number(declineJobId));
    if (!found?.id) return null;

    return (
      <ConfirmationModal
        open={!!declineJobId}
        title={translate("declineQuote")}
        closeModal={() => setDeclineJobId(0)}
        loading={decliningQuote}
        submit={() => declineQuote(found)}
        textSubmit={translate("decline")}
      >
        <Grid container className={classes.approveContent}>
          <Grid item>
            <span className={classes.approveDescription}>
              {translate("doYouReallyWantToDeclineTheQuote")}
            </span>
          </Grid>
        </Grid>
      </ConfirmationModal>
    );
  };

  const declineButton = (_id: string | number, quoteItem: Job) => {
    if (
      !viewOnly &&
      isJobQuote(quoteItem) &&
      isSubStatusAvailableToActionsQuote(quoteItem?.subStatus)
    ) {
      return (
        <Tooltip title={translate("decline") || ""} placement="top">
          <div>
            <BiDislike
              onClick={() => setDeclineJobId(Number(quoteItem?.id))}
              className={classes.button}
              color="#F97066"
              size={Constants.defaultGeneralActionsIconSize}
            />
          </div>
        </Tooltip>
      );
    }
    return null;
  };

  const handleViewButton = (_id: string | number, quoteItem: Job) => {
    return (
      <Tooltip data-cy="action-view" title={translate("view")} placement="top">
        <div>
          <FiEye
            onClick={() => {
              setToViewQuote(quoteItem);
              setRecordingsFiles([]);
              setScreenshotsFiles([]);
              sideViewRef.current?.setOpenSideBar(true);
            }}
            color="#475467"
            className={classes.button}
            size={Constants.defaultGeneralActionsIconSize}
          />
        </div>
      </Tooltip>
    );
  };

  const handleEditButton = (id: string | number, quoteItem: Job) => {
    if (!viewOnly && quoteItem && isJobQuote(quoteItem)) {
      return (
        <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
          <div>
            <FiEdit
              onClick={() => (handleEdit ? handleEdit(Number(id), quoteItem) : editTask(id))}
              color="#475467"
              className={classes.button}
              size={Constants.defaultGeneralActionsIconSize}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const sendToCustomerRef = useRef<SendToCustomerHandle>(null);
  const communicationModalRef = useRef<SendToCustomerQuoteHandle>(null);
  const handleCommunication = (id: string | number, quoteItem: Job) => {
    if (
      !viewOnly &&
      quoteItem &&
      isJobQuote(quoteItem) &&
      isSubStatusAvailableToActionsQuote(quoteItem?.subStatus)
    ) {
      return (
        <Tooltip data-cy="action-communication" title={translate("communication")} placement="top">
          <div>
            <HiOutlineMail
              onClick={() => selectTaskToSendingCommunications(Number(id))}
              className={classes.button}
              color="#475467"
              size={Constants.defaultGeneralActionsIconSize}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const extraOptions = () => {
    if (showCalendarFormat) {
      return (
        <Tooltip title={translate("viewInGridMode") || ""} placement="top">
          <div onClick={() => setShowCalendarFormat(false)} className={classes.circleIcon}>
            <FiMenu color="#93A9BD" />
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={translate("viewInCalendarMode") || ""} placement="top">
        <div onClick={() => setShowCalendarFormat(true)} className={classes.circleIcon}>
          <FiCalendar color="#93A9BD" />
        </div>
      </Tooltip>
    );
  };

  const getQuoteFilters = () => {
    return {
      categories: [
        { name: translate("type"), options: jobTypes?.map((jobType) => jobType?.name || "") },
        {
          name: translate("status"),
          column: "sub_status",
          options: jobStatus?.map((jobStatusItem) => jobStatusItem?.name || ""),
        },
        {
          name: translate("leadSource"),
          column: "lead_source_from",
          options: leadSourceFrom?.map((leadSource) => leadSource?.name || ""),
        },
      ],
      onChange: handleFilter,
    };
  };

  const setTags = (value: string) => {
    const tags = value.split(",");

    return (
      <TagWrapper>
        {tags.map((tag: string, index: number) => (
          <Chip key={`chip-${index}`} label={tag} />
        ))}
      </TagWrapper>
    );
  };

  const renderEnableQuoteEditing = (_id: number, quoteItem: Job) => {
    if (
      quoteItem.subStatus &&
      quoteItem.subStatus?.toLowerCase() === JobSubStatus.WON.toLowerCase()
    ) {
      return (
        <Tooltip title={translate("enableEstimateEditing") || ""} placement="top">
          <Icon
            onClick={() => {
              setQuoteToBeEdit(quoteItem);
              setEnableQuoteEditing(true);
            }}
          >
            <img src={PAPER_EDIT} width={18} height={18} alt={translate("enableEstimateEditing")} />
          </Icon>
        </Tooltip>
      );
    }

    return null;
  };

  useEffect(() => {
    if (isRefetchGetAll) refetchQuoteList();
  }, [isRefetchGetAll, refetchQuoteList]);

  const renderGridLoadingStatus = () => {
    if (loading) return `${translate("loadingQuotes")}...`;

    return (
      <NoDataContainer>
        <NoDataTitle>{translate("noInformationToBeShown")}</NoDataTitle>
      </NoDataContainer>
    );
  };

  const renderCalendarLoadingStatus = () => {
    if (isFetchingCalendar) return <SkeletonCommunication />;

    return (
      <NoDataContainer>
        <NoDataTitle>{translate("noInformationToBeShown")}</NoDataTitle>
      </NoDataContainer>
    );
  };

  const renderStatusOfData = () => {
    if (showCalendarFormat) return renderCalendarLoadingStatus();
    return renderGridLoadingStatus();
  };

  const handleRowActionsExtra = () => {
    let rowActionsExtra: RowActionsExtra[] = [];

    if (isShowAction(currentUser, quotePermission?.edit)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        { title: translate("edit"), key: "edit", buttonAndAction: handleEditButton },
      ];
    }

    rowActionsExtra = [
      ...rowActionsExtra,
      { title: translate("view"), key: "view", buttonAndAction: handleViewButton },
    ];

    if (isShowAction(currentUser, quotePermission?.communication)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        {
          title: translate("communication"),
          key: "communication",
          buttonAndAction: handleCommunication,
        },
      ];
    }

    if (isShowAction(currentUser, quotePermission?.videoSurvey)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        {
          title: translate("startVideoSurvey"),
          key: "videoSurvey",
          buttonAndAction: startVideoSurveyButton,
        },
      ];
    }

    rowActionsExtra = [
      ...rowActionsExtra,
      {
        title: translate("generateReport"),
        key: "generateReport",
        buttonAndAction: (_: any, row: Sales) => {
          const quoteHasVideoSurveyOrHomeSurvey = row?.tasks?.filter(
            (task) => task?.type === TaskType.VIDEO_SURVEY || task?.type === TaskType.HOME_SURVEY
          );

          if (!quoteHasVideoSurveyOrHomeSurvey?.length) return null;

          return (
            <Tooltip title={translate("generateReport")} placement="top">
              <div>
                <FiPrinter
                  size={Constants.defaultGeneralActionsIconSize}
                  className={classes.button}
                  data-cy="inventoryReportButton"
                  color="rgb(71, 84, 103)"
                  onClick={() =>
                    generateReportRef.current?.open({
                      id: row.id,
                      reference: row.reference,
                      customer: row.customer,
                      type: row.type,
                      status: row.status,
                    } as Job)
                  }
                />
              </div>
            </Tooltip>
          );
        },
      },
    ];

    if (isShowAction(currentUser, quotePermission?.approve)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        { title: translate("approve"), key: "approve", buttonAndAction: approveButton },
      ];
    }

    if (isShowAction(currentUser, quotePermission?.decline)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        { title: translate("decline"), key: "decline", buttonAndAction: declineButton },
      ];
    }

    if (isShowAction(currentUser, quotePermission?.cancel)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        {
          title: translate("cancelQuote"),
          key: "cancel",
          buttonAndAction: renderCancelQuoteButton,
        },
      ];
    }

    if (isShowAction(currentUser, quotePermission?.editEstimate)) {
      rowActionsExtra = [
        ...rowActionsExtra,
        {
          title: translate("enableQuoteEditing"),
          key: "enableQuoteEditing",
          buttonAndAction: renderEnableQuoteEditing,
        },
      ];
    }

    rowActionsExtra.push({
      title: translate("seeMore"),
      key: "seeMore",
      buttonAndAction: renderSeeMore,
    });

    return rowActionsExtra;
  };

  const renderGrid = () => {
    if (
      !isShowAction(currentUser, quotePermission?.viewAll) &&
      !isShowAction(currentUser, quotePermission?.viewOwn)
    ) {
      return null;
    }

    let gridQuotes = listQuotes;
    let calendarQuotes = quotesCalendar;

    if (!isShowAction(currentUser, quotePermission?.viewAll)) {
      gridQuotes = listQuotes?.filter((q: any) => q?.user?.email === currentUser?.email);
      calendarQuotes = quotesCalendar?.filter((q: any) => q?.user?.email === currentUser?.email);
    }

    return (
      <ShipeeziGrid
        hasComponent={showCalendarFormat}
        component={() => (
          <Calendar
            quotes={calendarQuotes}
            actions={{
              handleEdit: (rowId: any) => editTask(rowId),
              handleStartVideoSurvey: (tasks: Task[]) => {
                setTasksSelected(tasks);
                setOpenModalSelectSurveyTask(true);
              },
              handleApprove: (id: number | string) => setApproveJobId(Number(id)),
              handleDecline: (id: number | string) => setDeclineJobId(Number(id)),
              handleCommunication: (id: number | string) => selectTaskToSendingCommunications(id),
            }}
          />
        )}
        columns={[
          {
            name: "type",
            title: translate("type"),
            formatComponent: showBadgeType,
            hideSearch: true,
          },
          {
            name: "mode",
            title: translate("mode"),
            hideSearch: true,
            formatComponent: (value: string) =>
              value ? translate(value.toLowerCase()) : "",
          },
          { name: "reference", title: translate("reference") },
          {
            name: "secondReference",
            title: translate("secondReference"),
            formatComponent: (value?: string) => `${value || "--"}`,
            initialHideColumn: true,
            searchIdentifier: "second_reference",
          },
          {
            name: "customer",
            title: translate("customer"),
            hideSort: disableCustomerSorting,
            formatText: (customer: Customer) => (customer?.fullName ? customer.fullName : ""),
            associationColumnSettings: {
              whenSearching: {
                clearSorting: setSorting,
                disableSorting: setDisableCustomerSorting,
              },
              columnToSorting: "full_name",
            },
          },
          {
            name: "user",
            title: translate("consultant"),
            hideSort: true,
            formatText: (user: User) => (user?.fullName ? user.fullName : ""),
            associationColumnSettings: {
              whenSearching: {
                clearSorting: setSorting,
                disableSorting: setDisableCustomerSorting,
              },
              columnToSorting: "name",
            },
          },
          {
            name: "volume",
            title: translate("volume"),
            hideSort: true,
            hideSearch: true,
            formatComponent: (_: any, job: Job) => {
              if (job?.customVolumeQuoted === null || job?.customVolumeQuoted === undefined)
                return calculateTotalVolume(
                  ItemJobSubStatus.QUOTED,
                  measurement,
                  job?.itemsJob,
                  job?.materials
                );

              return `${handleGetConvertValue(
                job?.customVolumeQuoted || 0,
                measurement?.code,
                measurement?.decimalPlaces
              )} ${measurement?.code || Metric["m³"]}`.trim();
            },
          },
          {
            name: "origins",
            title: translate("origin"),
            hideSort: true,
            formatComponent: renderOrigin,
          },
          {
            name: "destinations",
            title: translate("destination"),
            hideSort: true,
            formatComponent: renderDestination,
          },
          {
            name: "tag",
            title: translate("tags"),
            formatComponent: (value: string) => (value?.trim().length ? setTags(value) : ""),
          },
          {
            name: "updatedAt",
            title: translate("lastUpdated"),
            hideSearch: true,
            hideSort: true,
            formatText: (value: any) => (value?.trim().length ? formatDateWithUserPref(value) : ""),
          },
          {
            name: "subStatus",
            title: translate("status"),
            formatComponent: showBadgeSubStatus,
            hideSearch: true,
          },
        ].map((column: any) => ({
          ...column,
          initialHideColumn:
            columnsToShow && columnsToShow.length
              ? !columnsToShow.includes(column.name)
              : column.initialHideColumn,
        }))}
        data={gridQuotes}
        totalItems={total}
        loadingData={loading}
        namePage={translate("quotes")}
        emptyData={renderStatusOfData}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        showFooterGrid={!showCalendarFormat}
        showFooterPageInfo={!showCalendarFormat}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: orderByGeneralActions || ["handleNewRecord", "handleCalendarMode"],
          },
          handleCalendarMode: extraOptions(),
          handleNewRecord: isShowAction(currentUser, quotePermission?.create)
            ? {
                text: translate("create"),
                action: () =>
                  handleNewRecord ? handleNewRecord() : history.push(PRIVATE_ROUTES.QUOTES_MNT),
                tooltip: translate("create"),
              }
            : undefined,
          handleSearch: { onSearch: (filtersValue) => handleSearch(filtersValue) },
          handleFilterBy: showCalendarFormat ? undefined : getQuoteFilters(),
        }}
        onChangeColumns={(columns) =>
          window.localStorage.setItem(SELECTED_COLUMNS_QUOTES, JSON.stringify(columns))
        }
        onSorting={(sorts: Sort[]) => handleSort(sorts)}
        rowActions={{
          orderBy: [
            "edit",
            "view",
            "videoSurvey",
            "communication",
            "generateReport",
            "approve",
            "decline",
            "cancel",
            "seeMore",
            "enableQuoteEditing",
          ],
        }}
        rowActionsExtra={handleRowActionsExtra()}
        positionActionsColumn="right"
      />
    );
  };

  const [destinationList, setDestinationList] = useState<Destination[]>([]);

  useQuery(
    ["destination.list"],
    () => DestinationService.getAll({ sortBy: JSON.stringify(["name"]) }),
    {
      enabled: !toViewQuote?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response?.data) setDestinationList(response.data);
      },
    }
  );

  const getSidebarTitle = (): string => {
    let title = "Quote";
    const reference = toViewQuote?.reference ?? quote?.reference;
    const fullName = toViewQuote?.customer?.fullName ?? quote?.origin?.customer?.fullName;

    if (reference) title = `${title} #${reference}`;
    if (fullName) title = `${title} - ${fullName}`;

    return title;
  };

  const handleSetNavigation = useCallback((data?: Sales) => {
    const navList = [
      { key: Navigation.DETAILS },
      { key: Navigation.INVENTORY },
      { key: Navigation.MATERIAL },
      { key: Navigation.DATE },
      { key: Navigation.ACCESS },
      { key: Navigation.INSTRUCTIONS },
      { key: Navigation.FOLDERS },
    ];

    navList.splice(6, 0, { key: Navigation.SUMMARY });

    if (data?.tasks?.find((task) => task.id && task.type === TaskType.VIDEO_SURVEY))
      navList.splice(3, 0, { key: Navigation.VIDEO_SURVEY });

    return navList;
  }, []);

  const handleSetTabs = useCallback(
    (data?: Sales) => {
      const tabs = [
        {
          key: Navigation.DETAILS,
          loading: isFetching,
          isEmpty: !quote,
          isOpened: false,
          render: () => <DetailsViewMode quote={quote} />,
          renderLoading: () => <LoadingSkeleton />,
        },
        {
          key: Navigation.INVENTORY,
          loading: isFetching || isLoadingInventory,
          renderLoading: () => <LoadingSkeleton />,
          isEmpty: !quote?.inventory || quote?.inventory?.length === 0,
          render: () => (
            <Inventory
              inventory={quote?.inventory}
              quoteJobId={quote?.jobId || 0}
              defaultMode={quote?.mode || ""}
              destinations={quote?.destination?.locations || []}
              isOnViewMode
            />
          ),
        },
        {
          key: Navigation.MATERIAL,
          loading: isFetching || isLoadingInventory,
          isEmpty: !quote?.inventory || quote?.inventory?.length === 0,
          renderLoading: () => <LoadingSkeleton />,
          render: () => <PackingMaterials inventory={quote?.inventory} isOnViewMode />,
        },
        {
          key: Navigation.DATE,
          loading: isFetching,
          isEmpty:
            (!quote?.tasks && !quote?.taskDates) ||
            (quote?.tasks?.length === 0 && quote?.taskDates?.length === 0),
          renderLoading: () => <LoadingSkeleton />,
          render: () => <Tasks tasks={quote?.tasks} taskDates={quote?.taskDates} isOnViewMode />,
        },
        {
          key: Navigation.ACCESS,
          loading: isFetching,
          renderLoading: () => <LoadingSkeleton />,
          isEmpty: !quote?.pickupAccess && !quote?.deliveryAccess,
          render: () => (
            <AccessTab
              pickupAccess={quote?.pickupAccess}
              deliveryAccess={quote?.deliveryAccess}
              pickupAddress={quote?.origin?.locations?.[0]}
              deliveryAddress={quote?.destination?.locations?.[0]}
            />
          ),
        },
        {
          key: Navigation.INSTRUCTIONS,
          loading: isFetching,
          isEmpty: !quote?.instructions || quote?.instructions.length === 0,
          renderLoading: () => <LoadingSkeleton />,
          render: () => <Instructions isOnViewMode instructions={quote?.instructions || []} />,
        },
        {
          key: Navigation.FOLDERS,
          isEmpty: false,
          render: () => (
            <Files
              isOnViewMode
              folders={newFoldersToRender}
              foldersDeleted={quote?.foldersDeleted}
              filesDeleted={quote?.filesDeleted}
              inventory={quote?.inventory}
            />
          ),
        },
      ];

      tabs.splice(6, 0, {
        key: Navigation.SUMMARY,
        isEmpty: false,
        render: () => <SummaryTab isOnViewMode data={quote} externalList={{ destinationList }} />,
      });

      if (data?.tasks?.find((task) => task.id && task.type === TaskType.VIDEO_SURVEY)) {
        tabs.splice(3, 0, {
          key: Navigation.VIDEO_SURVEY,
          loading: !quote,
          isEmpty: false,
          isOpened: quote?.openedView,
          renderLoading: () => <LoadingSkeleton />,
          render: () => (
            <>
              <VideoSurvey
                isOnViewMode
                taskId={quote?.tasks?.find((task) => task.type === Navigation.VIDEO_SURVEY)?.id}
                tasks={quote?.tasks?.filter((task) => task.type === Navigation.VIDEO_SURVEY)}
              />
              {openModal && (
                <Modal
                  className={classes.confirmModal}
                  open={openModal}
                  type={ModalType.LEAVE}
                  title={translate("videoSurveyRecordDeleted")}
                  showActionButtons
                  handleClose={(_, reason) => {
                    const hasToOpen = localStorage.getItem("hasToOpen");
                    if (hasToOpen === "true") localStorage.setItem("hasToOpen", "false");
                    if (reason !== "backdropClick") setOpenModal(false);
                  }}
                  actionButtonProps={{
                    textSave: translate("understood"),
                    customColorSave: Color.WARNING,
                    showCancelButton: false,
                    loading: mutate.isLoading,
                    onSave: () => mutate.mutate(),
                  }}
                >
                  <p className={classes.messageModal}>
                    {translate("theVideoRecordedForThisSurveyWasDeletedAsTheStatusHasChangedTo")}{" "}
                    <b>{quote?.jobSubStatus}</b>.
                  </p>
                  <span className={classes.subMessageModal}>
                    {translate("youDoNotHaveAccessToThisRecordAnymore")}
                  </span>
                </Modal>
              )}
            </>
          ),
        });
      }

      return tabs;
    },
    [
      quote,
      openModal,
      mutate.isLoading,
      isFetching,
      isLoadingInventory,
      destinationList,
      newFoldersToRender,
    ]
  );

  return (
    <div className={className || classes.mainDiv}>
      {renderSelectVideoSurveyModal()}
      {renderApproveConfirmationModal()}
      {renderDeclineConfirmationModal()}
      {renderReScheduleVideoSurveyModal()}
      {renderEnableQuoteEditingModal()}
      {renderCancelOrder()}

      <CancelQuoteConfirmationModal
        ref={cancelModalRef}
        loadingAction={loadingCancelQuote}
        showReason={true}
        onSubmit={cancelQuoteSubmit}
      />
      {/* {showTitle !== false && <TitlePage title={translate("quotes")} />} */}

      <div data-cy="grid-quotes" className={classNameContent || classes.content}>
        {renderGrid()}
      </div>

      <Menu
        id="long-menu"
        anchorEl={anchorMoreIcon}
        keepMounted
        open={!!(anchorMoreIcon && quoteIdSend)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setAnchorMoreIcon(null)}
      >
        <MenuItem
          key="sendToAmos"
          onClick={() => {
            sendToAMOS({ jobId: Number(quoteIdSend) });
            setAnchorMoreIcon(null);
          }}
        >
          <span className={classes.menuItemText}>{translate("sendToAmos")}</span>
        </MenuItem>
      </Menu>

      <SideViewComponent
        ref={sideViewRef}
        job={{ ...quote, folders: newFoldersToRender }}
        setOpenModal={(value: boolean) => setOpenModal(value)}
        isLoading={isFetching}
        title={getSidebarTitle()}
        tabs={handleSetTabs(quote)}
        navigation={handleSetNavigation(quote)}
        onClose={() => setToViewQuote(undefined)}
        buttonsComponent={[
          {
            buttonLabel: translate("seeActivity"),
            titlePage: translate("activity"),
            disabled: isFetching,
            color: Color.SECONDARY,
            icon: <FiActivity size={16} />,
            renderLoading: () => <LoadingSkeleton />,
            renderComponent: () => (
              <History
                items={getItemsToHistory(
                  currentUser ?? ({} as User),
                  quote?.jobId,
                  quote?.histories
                )}
              />
            ),
          },
        ]}
      />
      <SendToCustomer
        ref={sendToCustomerRef}
        tasks={tasksSelected}
        onRefetch={() => {
          refetchCountList();
          refetchQuoteList();
        }}
      />

      <SendToCustomerModalQuote
        ref={communicationModalRef}
        onRefetch={() => {
          refetchCountList();
          refetchQuoteList();
        }}
      />

      <GenerateReportDrawer ref={generateReportRef} />
    </div>
  );
};

export default Quote;
